import axios from 'axios';
import _isEmpty from 'lodash/isEmpty';
import { projectRequest } from 'store/utils/request';
import {
  partialExportAction,
  cancelPartialExportAction,
  partialImportAndMergeAction,
  partialImportAndReplaceAction,
} from 'store/shared/actions';

import MESSAGES from 'constants/messages';
import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import { getCurrentPage } from 'store/utils/pagination';
import { getEntitiesConnectedDictionariesList } from 'store/services/entitiesDictionaries/actions';
import { gatherDictionariesConnectedIds } from 'store/services/entitiesDictionaries/selectors';

import {
  calculateFinalParams,
  calculateNewFilterOption,
} from 'store/utils/handleParams';

import * as api from './api';
import * as types from './actionTypes';
import * as selectors from './selectors';

const getAllEntitiesRequestId = 'get-entities';

/**
 * Check if an entity already exists
 *
 * @param fieldName
 * @param fieldValue
 * @param exception
 * @returns {Function}
 */
export const checkEntityName = (
  fieldName,
  fieldValue,
  exception = null,
) => async () => {
  try {
    const { data } = await api.checkEntityNameRequest(fieldValue, exception);

    if (data.count > 0) {
      // eslint-disable-next-line no-throw-literal
      throw {
        [fieldName]: MESSAGES.ENTITIES.NAME.EXISTS,
      };
    }
    if (data.count_reserved > 0) {
      // eslint-disable-next-line no-throw-literal
      throw {
        [fieldName]: MESSAGES.ENTITIES.NAME.RESERVED,
      };
    }
  } catch (err) {
    throw err;
  }
};

export const cancelGetAllEntities = () => dispatch => {
  projectRequest.cancel(getAllEntitiesRequestId);
  dispatch({
    type: types.CANCEL_GET_ENTITIES,
  });
};
/**
 * Get All entities
 *
 * @param params
 */
export const getAllEntities = (params = {}) => async dispatch => {
  try {
    dispatch({
      type: types.GET_ENTITY_ALL_REQUEST,
    });

    cancelGetAllEntities();
    const { data } = await api.getEntitiesRequest(
      params,
      getAllEntitiesRequestId,
    );
    dispatch({
      type: types.GET_ENTITY_ALL_SUCCESS,
      payload: {
        items: data.results,
      },
    });
  } catch (error) {
    if (!axios.isCancel(error)) {
      dispatch({
        type: types.GET_ENTITY_ALL_ERROR,
        payload: {
          error: error.message,
        },
      });

      Notify.error({
        title: 'An error occurred',
        description: ErrorHandler.getNotificationError(error),
      });
    }
  }
};

/**
 * Get paginated entities
 *
 * @param paginationParams
 * @param filterParams
 */
export const getEntitiesList = (
  paginationParams = {},
  filterParams = {},
) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.GET_ENTITY_LIST_REQUEST,
    });
    const state = getState();
    let filterOpt = filterParams;

    if (_isEmpty(filterOpt)) {
      filterOpt = selectors.getFilterParams(state);
    }

    const paginationOptions = {
      ...selectors.getPaginationOptions(state),
      ...paginationParams,
    };
    const filterOptions = {
      ...selectors.getFilterParams(state),
      ...filterOpt,
    };

    const finalParams = calculateFinalParams(filterOptions, paginationOptions);

    const { data } = await api.getEntitiesRequest(finalParams);

    dispatch({
      type: types.GET_ENTITY_LIST_SUCCESS,
      payload: {
        items: data.results,
        count: data.count,
      },
    });

    dispatch({
      type: types.UPDATE_ENTITY_LIST_OPTIONS,
      payload: {
        paginationOptions,
        filterOptions: calculateNewFilterOption(
          selectors.getFilterOptions(state),
          filterOpt,
        ),
        filterParams: filterOptions,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_ENTITY_LIST_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Get entity
 *
 * @param id
 */
export const getEntity = id => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.GET_ENTITY_REQUEST,
    });

    const { data } = await api.getEntityRequest(id);

    dispatch({
      type: types.GET_ENTITY_SUCCESS,
      payload: {
        item: data,
      },
    });

    const dictionariesConnectedIds = await gatherDictionariesConnectedIds(
      getState(),
    );

    if (dictionariesConnectedIds) {
      await dispatch(
        await getEntitiesConnectedDictionariesList({
          id__in: dictionariesConnectedIds,
        }),
      );
    }
  } catch (error) {
    dispatch({
      type: types.GET_ENTITY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add an entity
 *
 * @param data
 */
export const addEntity = data => async dispatch => {
  try {
    dispatch({
      type: types.ADD_ENTITY_REQUEST,
    });

    const response = await api.addEntityRequest(data);

    dispatch({
      type: types.ADD_ENTITY_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    await dispatch(getEntitiesList({ page: 1 }));

    Notify.success({
      title: 'Entity was created successfully',
    });

    return Promise.resolve(response.data.id);
  } catch (error) {
    dispatch({
      type: types.ADD_ENTITY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to create Entity',
      description: ErrorHandler.getNotificationError(error),
    });

    return undefined;
  }
};

export const updateEntity = (id, data) => async dispatch => {
  try {
    dispatch({
      type: types.UPDATE_ENTITY_REQUEST,
    });

    const response = await api.updateEntityRequest(id, data);

    dispatch({
      type: types.UPDATE_ENTITY_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    await dispatch(getEntitiesList());

    Notify.success({
      title: 'Entity was updated successfully',
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_ENTITY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Entity',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Delete an entity
 *
 * @param id
 */
export const deleteEntity = id => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.DELETE_ENTITY_REQUEST,
    });

    await api.deleteEntityRequest(id);

    dispatch({
      type: types.DELETE_ENTITY_SUCCESS,
    });

    await dispatch(
      getEntitiesList({
        page: getCurrentPage(
          selectors.getPaginationOptions(getState()).page,
          selectors.getEntitiesList(getState()),
        ),
      }),
    );

    Notify.success({
      title: 'Entity was deleted successfully',
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_ENTITY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to delete Entity',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const partialExportSingleEntity = partialExportAction({
  path: 'entities',
  filename: 'entity',
  types: {
    complete: types.PARTIAL_EXPORT_ENTITY_COMPLETE,
    request: types.PARTIAL_EXPORT_ENTITY_REQUEST,
  },
});

export const partialExportEntities = partialExportAction({
  path: 'entities',
  types: {
    complete: types.PARTIAL_EXPORT_ENTITY_COMPLETE,
    request: types.PARTIAL_EXPORT_ENTITY_REQUEST,
  },
});

export const cancelExportEntities = cancelPartialExportAction({
  path: 'entities',
  types: {
    cancel: types.PARTIAL_EXPORT_ENTITY_CANCEL,
  },
});

export const partialImportAndMergeEntities = partialImportAndMergeAction({
  path: 'entities',
  postHook: dispatch => dispatch(getEntitiesList({ page: 1 })),

  types: {
    complete: types.PARTIAL_IMPORT_ENTITY_COMPLETE,
    request: types.PARTIAL_IMPORT_ENTITY_REQUEST,
  },
});

export const partialImportAndReplaceEntities = partialImportAndReplaceAction({
  path: 'entities',
  postHook: dispatch => dispatch(getEntitiesList({ page: 1 })),

  types: {
    complete: types.PARTIAL_IMPORT_ENTITY_COMPLETE,
    request: types.PARTIAL_IMPORT_ENTITY_REQUEST,
  },
});

/**
 * Select a entity
 *
 * @param item
 */

export const selectEntity = item => (dispatch, getState) => {
  if (item.id !== selectors.getEntity(getState()).id) {
    dispatch({
      type: types.SELECT_ENTITY,
      payload: {
        item,
      },
    });
  }
};

/**
 * Deselect a entity
 */
export const deselectEntity = () => ({
  type: types.DESELECT_ENTITY,
});
